export type ReceiptTranslate = (
  key: string,
  options?: Record<string, unknown>
) => string;

export interface ReceiptRow {
  id: string;
  label: string;
  value: string;
  emphasis?: boolean;
}

export interface CanonicalReference {
  type: string;
  value: string;
  label?: Record<string, string>;
  presentation: string;
  issuedAt?: string;
}

/** Header + money fields the PDF masthead needs. */
export interface ReceiptOperation {
  operationId: string;
  status: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  recipient?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
  references?: CanonicalReference[];
  createdAt: string;
}

export interface ReceiptOrigination {
  originationId: string;
  createdAt: string;
  sendCommitment: {
    sendAmount: number;
    totalDebit: number;
    sendCurrency: string;
    destinationCurrency: string;
  };
  recipientHint: {
    firstName: string;
    lastName: string;
  };
  operation?: {
    operationId: string;
    status: string;
    sendAmount: number;
    receiveAmount: number;
    exchangeRate: number;
    fee?: number;
    fundingFee?: number;
    transactionFee?: number;
    taxFee?: number;
    totalFee?: number;
    totalCost?: number;
    sourceCurrency: string;
    destinationCurrency: string;
    createdAt: string;
    references?: CanonicalReference[];
  } | null;
}

export const formatReceiptDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatReceiptDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return `${formatReceiptDate(date)}\n${date.toLocaleTimeString()}`;
};

/** Funds settle on the 5th business day after the operation is created. */
export const availableReceiptDate = (isoDate: string): string => {
  const result = new Date(isoDate);
  let added = 0;
  while (added < 5) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return formatReceiptDate(result);
};

const formatAmount = (
  amount: number,
  _lang: string,
  digits?: { min?: number; max?: number }
): string =>
  new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: digits?.min ?? 2,
    maximumFractionDigits: digits?.max ?? 2,
  }).format(amount);

export const formatReceiptMoney = (
  amount: number | undefined,
  currency: string,
  lang = "en"
): string => {
  const formatted = formatAmount(amount ?? 0, lang);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
};

export const referenceLabel = (
  type: string,
  label: Record<string, string> | undefined,
  t: ReceiptTranslate,
  lang: string
): string => {
  const fromEnvelope = label?.[lang] || label?.en;
  if (fromEnvelope) return fromEnvelope;
  const key = `references.${type}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return type;
};

export const splitVisibleReferences = (
  references: CanonicalReference[] | undefined
): { prominent: CanonicalReference[]; secondary: CanonicalReference[] } => {
  const prominent: CanonicalReference[] = [];
  const secondary: CanonicalReference[] = [];
  for (const ref of references ?? []) {
    if (ref.presentation === "INTERNAL") continue;
    if (ref.presentation === "PROMINENT") {
      prominent.push(ref);
    } else {
      secondary.push(ref);
    }
  }
  return { prominent, secondary };
};

/** Map origination (+ optional bound operation) to the PDF header shape. */
export const toReceiptOperation = (
  detail: ReceiptOrigination
): ReceiptOperation => {
  const op = detail.operation;
  const sc = detail.sendCommitment;
  return {
    operationId: op?.operationId ?? detail.originationId,
    status: op?.status ?? "",
    sendAmount: op?.sendAmount ?? sc.sendAmount,
    receiveAmount: op?.receiveAmount ?? 0,
    exchangeRate: op?.exchangeRate ?? 0,
    fee: op?.fee,
    fundingFee: op?.fundingFee,
    transactionFee: op?.transactionFee,
    taxFee: op?.taxFee,
    totalFee: op?.totalFee,
    totalCost: op?.totalCost ?? sc.totalDebit,
    sourceCurrency: op?.sourceCurrency ?? sc.sendCurrency,
    destinationCurrency: op?.destinationCurrency ?? sc.destinationCurrency,
    createdAt: op?.createdAt ?? detail.createdAt,
    references: op?.references,
    recipient: {
      name: {
        firstName: detail.recipientHint.firstName,
        lastName: detail.recipientHint.lastName,
      },
    },
  };
};

/**
 * Single source of receipt rows for the on-screen card and the vector PDF.
 * Status label is resolved by the page (binding + cash-deposit).
 */
export const buildReceiptRows = (
  detail: ReceiptOrigination,
  t: ReceiptTranslate,
  opts: { lang: string; statusLabel: string }
): ReceiptRow[] => {
  const sc = detail.sendCommitment;
  const op = detail.operation;
  const source = op?.sourceCurrency ?? sc.sendCurrency;
  const destination = op?.destinationCurrency ?? sc.destinationCurrency;
  const recipientName = [detail.recipientHint.firstName, detail.recipientHint.lastName]
    .filter(Boolean)
    .join(" ");
  const { secondary } = splitVisibleReferences(op?.references);
  const money = (amount: number | undefined, currency: string) =>
    formatReceiptMoney(amount, currency, opts.lang);

  const rows: ReceiptRow[] = [
    {
      id: "status",
      label: t("receipt.status"),
      value: opts.statusLabel,
      emphasis: true,
    },
  ];

  if (op) {
    rows.push(
      {
        id: "amountSent",
        label: t("receipt.amountSent"),
        value: money(op.sendAmount, source),
      },
      {
        id: "transactionFee",
        label: t("receipt.transactionFee"),
        value: money(op.transactionFee, source),
      },
      {
        id: "exchangeRate",
        label: t("receipt.exchangeRate"),
        value: `${formatAmount(op.exchangeRate, opts.lang, {
          min: 4,
          max: 4,
        })} ${destination} / ${source}`,
      },
      {
        id: "totalPayment",
        label: t("receipt.totalPayment"),
        value: money(op.totalCost, source),
      },
      {
        id: "amountReceived",
        label: t("receipt.amountReceived"),
        value: money(op.receiveAmount, destination),
      },
      {
        id: "fundingFee",
        label: t("receipt.fundingFee"),
        value: money(op.fundingFee ?? op.fee ?? op.totalFee, source),
      },
      {
        id: "transferTaxes",
        label: t("receipt.transferTaxes"),
        value: money(op.taxFee, source),
      }
    );
    if (recipientName) {
      rows.push({
        id: "recipient",
        label: t("receipt.recipient"),
        value: recipientName,
      });
    }
    rows.push(
      {
        id: "transactionDate",
        label: t("receipt.transactionDate"),
        value: formatReceiptDateTime(op.createdAt),
      },
      {
        id: "dateAvailable",
        label: t("receipt.dateAvailable"),
        value: availableReceiptDate(op.createdAt),
      }
    );
  } else {
    rows.push(
      {
        id: "amountSent",
        label: t("receipt.amountSent"),
        value: money(sc.sendAmount, source),
      },
      {
        id: "totalDebit",
        label: t("receipt.totalDebit"),
        value: money(sc.totalDebit, source),
      }
    );
    if (recipientName) {
      rows.push({
        id: "recipient",
        label: t("receipt.recipient"),
        value: recipientName,
      });
    }
    rows.push({
      id: "dateAvailable",
      label: t("receipt.dateAvailable"),
      value: formatReceiptDate(new Date(detail.createdAt)),
    });
  }

  for (const ref of secondary) {
    rows.push({
      id: `ref:${ref.type}`,
      label: referenceLabel(ref.type, ref.label, t, opts.lang),
      value: ref.value,
    });
  }

  return rows;
};
